import React, { useContext, useState } from 'react';
import BookDetailForm from './BookDetailForm';
import { BookDetail, BookContent, BookIllustrations, BookWithIllustrations, LanguageContextData } from './types';
import { retrieveBookContent, retrieveBookIllustrations, validateToken } from './loader';
import BookContentForm from './BookContentForm';
import BookContentPage from './BookContentPage';
import BookRenderingPage from './BookRenderingPage';
import TokenForm from './TokenForm';
import Disclaimer from './Disclaimer';
import LanguageSelection from './LanguageSelection';
import Header from './Header';
import { textValues, LanguageContext } from './utils';
import { FaExclamationTriangle } from 'react-icons/fa';
//const illustration_data = require('./illustration_data.json') as BookIllustrations;
//const texts_data = require('./text_data.json') as BookContent;

const START_PAGE = 1;
const TOKEN_PAGE = 2;
const DISCLAIMER_PAGE = 3;
const BOOK_DETAIL_PAGE = 4;
const BOOK_CONTENT_PAGE = 5;
const BOOK_ILLUSTRATIONS_PAGE = 6;
const BOOK_RENDERING_PAGE = 7;

const App = () => {
  const { language } = useContext<LanguageContextData>(LanguageContext);
  const [ token, setToken ] = useState<string>('');
  const [ error, setError ] = useState<string>();
  const [ bookDetail, setBookDetail ] = useState<BookDetail>();
  const [ bookContent, setBookContent ] = useState<BookContent | undefined>();
  const [ customizedBookContent, setCustomizedBookContent ] = useState<BookContent | undefined>();
  const [ bookIllustrations, setBookIllustrations ] = useState<BookIllustrations | undefined>();
  const [ bookWithIllustrations, setBookWithIllustrations] = useState<BookWithIllustrations>();
  const [ isLoading, setIsLoading ] = useState<boolean>(false);
  const [ step, setStep ] = useState<number>(START_PAGE);

  const setTokenValue = async (token: string) => {
    try {
      setIsLoading(true);
      const valid = await validateToken(token);
      if (!valid) {
        setError('Invalid Token - reach out to the admin to request a new one');
        setIsLoading(false);
      } else {
        setToken(token);
        setIsLoading(false);
        setStep(DISCLAIMER_PAGE);
      }
    } catch (error) {
      setError('' + error);
      setIsLoading(false);
    }
  };

  const loadBookContent = async (bookDetail: BookDetail) => {
    try {
      setBookDetail(bookDetail);
      setIsLoading(true);
      setBookContent(await retrieveBookContent(bookDetail, token, language));
      setIsLoading(false);
      setStep(BOOK_CONTENT_PAGE);
    } catch (error) {
      setError('' + error);
      setIsLoading(false);
    }
  };

  const loadBookIllustrations = async (bookContent: BookContent) => {
    try {
      setCustomizedBookContent(bookContent);
      setIsLoading(true);
      setBookIllustrations(await retrieveBookIllustrations({ ...bookContent, topic: bookDetail?.topic }, token, language));
      setIsLoading(false);
      setStep(BOOK_ILLUSTRATIONS_PAGE);
    } catch (error) {
      setError('' + error);
      setIsLoading(false);
    }
  };

  const setIllustrationIndices = (titleIndex: number, textIndices: number[]) => {
    if (!!!bookIllustrations || !!!customizedBookContent) {
      setError('No Illustrations or no text passed');
      return;
    }
    const titleIllustration = bookIllustrations.titleIllustrations[titleIndex];
    const textIllustrations = bookIllustrations.textIllustrations.map((illustrations, index) => illustrations[textIndices[index]]);
    setBookWithIllustrations({ title: customizedBookContent.title, titleIllustration, text: customizedBookContent.text, textIllustrations });
    setStep(BOOK_RENDERING_PAGE);
  };

  return (
    <div>
      <Header step={ step } maxStep={ BOOK_RENDERING_PAGE } />
      { !!!isLoading && !!!error && step === START_PAGE && 
        <LanguageSelection proceed={ () => setStep(TOKEN_PAGE) } />
      }
      { !!!isLoading && !!!error && step === TOKEN_PAGE && 
        <TokenForm setToken={ setTokenValue } />
      }
      { isLoading && !!!error && step === TOKEN_PAGE &&
        <>
          <p>{ textValues[language]['spinnerToken'] }</p>
          <div className='spinner'>&nbsp;</div>
        </>
      }
      { !!!isLoading && !!!error && step === DISCLAIMER_PAGE && 
        <Disclaimer proceed={ () => setStep(BOOK_DETAIL_PAGE) } />
      }
      { !!!isLoading && !!!error && step === BOOK_DETAIL_PAGE && 
        <BookDetailForm setBookDetail={ loadBookContent } />
      }
      { isLoading && !!!error && step === BOOK_DETAIL_PAGE &&
        <>
          <p>{ textValues[language]['spinnerWriting'] }</p>
          <div className='spinner'>&nbsp;</div>
        </>
      }
      { !!!isLoading && !!!error && step === BOOK_CONTENT_PAGE && !!bookContent && 
        <BookContentForm bookContent={ bookContent } setBookContent={ loadBookIllustrations } />
      }
      { isLoading && !!!error && step === BOOK_CONTENT_PAGE &&
        <>
          <p>{ textValues[language]['spinnerDrawing'] }</p>
          <div className='spinner'>&nbsp;</div>
        </>
      }
      { !!!isLoading && !!!error && step === BOOK_ILLUSTRATIONS_PAGE && !!customizedBookContent && !!bookIllustrations &&
        <BookContentPage bookContent={ customizedBookContent } bookIllustrations={ bookIllustrations } setIllustrationIndices={ setIllustrationIndices } />
      }
      { !!!error && step === BOOK_RENDERING_PAGE && !!bookWithIllustrations &&
        <BookRenderingPage bookWithIllustrations={ bookWithIllustrations } />
      }
      { error && 
        <div className='processError'><FaExclamationTriangle /><b>{ textValues[language]['error'] }</b> { error }</div>
      }
    </div>
  );
};

export default App;